import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
const API_BASE_URL = process.env.REACT_APP_API_URL;

export const createBlog = createAsyncThunk(
  'blogs/createBlog',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}dashboard/create-blog`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating blog:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteBlog = createAsyncThunk(
  'blogs/deleteBlog',
  async (blogId, { rejectWithValue }) => {
    try {
      await axios.delete(
        `${API_BASE_URL}dashboard/delete-blog/${blogId}`
      );
      return blogId;
    } catch (error) {
      console.error('Error deleting blog:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateBlog = createAsyncThunk(
  'blogs/updateBlog',
  async ({ blogId, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}dashboard/update-blog/${blogId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating blog:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchBlogs = createAsyncThunk(
  'blogs/fetchBlogs',
  async ({ page, limit, search, deleted = '', homepage = '' }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}dashboard/get-all-dashboard-blogs?page=${page}&limit=${limit}&search=${search}&deleted=${deleted}&homepage=${homepage}`
      );
      return {
        items: response.data.items,
        total: response.data.totalItems,
        currentPage: response.data.page,
        pageSize: limit,
        totalPages: Math.ceil(response.data.totalItems / limit),
        itemCount: response.data.items.length
      };
    } catch (error) {
      console.error('Error fetching blogs:', error.response?.data?.message || error.message);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const setHomepageOrder = createAsyncThunk(
  'blogs/setHomepageOrder',
  async ({ blogId, order }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}dashboard/set-blog-homepage-order/${blogId}`,
        { order },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return {
        blogId,
        order,
        message: response.data.message,
        replacedBlog: response.data.replacedBlog || null
      };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to update position',
        status: error.response?.status
      });
    }
  }
);

const blogSlice = createSlice({
  name: 'blogs',
  initialState: {
    blogs: [],
    total: 0,
    loading: false,
    error: null,
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    itemCount: 0,
    success: false
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Create Blog
      .addCase(createBlog.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createBlog.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.blogs.unshift({ ...action.payload, status: 'active' });
        state.total += 1;
        state.totalPages = Math.ceil(state.total / state.pageSize);
      })
      .addCase(createBlog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Blog
      .addCase(updateBlog.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateBlog.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const index = state.blogs.findIndex(p => p.id === action.payload.blogId);
        if (index !== -1) {
          state.blogs[index] = { ...state.blogs[index], ...action.payload, status: 'active' };
        }
      })
      .addCase(updateBlog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Blogs
      .addCase(fetchBlogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBlogs.fulfilled, (state, action) => {
        state.loading = false;
        state.blogs = action.payload.items;
        state.total = action.payload.total;
        state.currentPage = action.payload.currentPage;
        state.pageSize = action.payload.pageSize;
        state.totalPages = action.payload.totalPages;
        state.itemCount = action.payload.itemCount;
      })
      .addCase(fetchBlogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete Blog
      .addCase(deleteBlog.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteBlog.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.blogs = state.blogs.map(blog =>
          blog.id === action.payload
            ? { ...blog, status: 'deleted', homepage_order: 0 }
            : blog
        );
      })
      .addCase(deleteBlog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Set Homepage Order
      .addCase(setHomepageOrder.fulfilled, (state, action) => {
        const { blogId, order } = action.payload;
        state.blogs = state.blogs.map(blog =>
          blog.id === blogId
            ? { ...blog, homepage_order: order }
            : blog
        );
        if (action.payload.replacedBlog) {
          state.blogs = state.blogs.map(blog =>
            blog.id === action.payload.replacedBlog.id
              ? { ...blog, homepage_order: 0 }
              : blog
          );
        }
      })
      .addCase(setHomepageOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default blogSlice.reducer;